<template>
    <div class="agent-profile-page">
        <!-- Breadcrumb -->
        <div class="ap-breadcrumb">
            <a href="/">Home</a>
            <span class="ap-breadcrumb-sep">›</span>
            <a href="/about">About Us</a>
            <span class="ap-breadcrumb-sep">›</span>
            <span>{{ agent_name }}</span>
        </div>

        <!-- Hero -->
        <div class="ap-hero">
            <div class="ap-hero-inner">
                <div class="ap-hero-photo-wrap">
                    <div class="ap-hero-photo-frame"></div>
                    <img class="ap-hero-photo" :src="agent_image" :alt="agent_name"/>
                </div>
                <div class="ap-hero-text">
                    <span class="ap-hero-label">Forman, Jones &amp; Associates · Realtors</span>
                    <h1 class="ap-hero-name" v-if="agent_name">
                        {{ firstName }} <em>{{ lastName }}</em>
                    </h1>
                    <p class="ap-hero-title">Licensed Real Estate Agent · Louisville, KY</p>
                    <div class="ap-hero-actions">
                        <a href="#" class="ap-btn-primary" data-toggle="modal" data-target="#contactModal">
                            Contact {{ firstName }}
                        </a>
                        <a href="#listings" class="ap-btn-ghost">View Listings</a>
                    </div>
                </div>
            </div>
        </div>

        <!-- Page Body -->
        <div class="ap-body">
            <div class="ap-main-col">

                <!-- About / Bio -->
                <section v-if="agent.bio && !editable">
                    <span class="ap-section-label">About</span>
                    <h2 class="ap-section-title">Get to know <em>{{ firstName }}</em></h2>
                    <div class="ap-about-text" v-html="agent.bio"></div>
                </section>

                <!-- Bio Editor -->
                <section v-if="editable">
                    <span class="ap-section-label">Edit Bio</span>
                    <h2 class="ap-section-title">Update your <em>profile</em></h2>
                    <vue-editor v-model="agent.bio"></vue-editor>
                    <div class="mt-3">
                        <button class="ap-btn-primary" @click="submitBio">Save Changes</button>
                        <button class="ap-btn-ghost-dark ml-2" @click="editable = false">Cancel</button>
                    </div>
                </section>

                <!-- Edit toggle -->
                <div v-if="canEdit && !editable" class="mt-3">
                    <button class="ap-btn-ghost-dark" @click="editable = true">
                        <i class="fas fa-pen"></i> Edit My Profile
                    </button>
                </div>

                <!-- Testimonials -->
                <section class="ap-testimonials-section" v-if="testimonials && testimonials.length > 0 && !editable">
                    <div class="ap-section-rule">
                        <div class="ap-section-rule-line"></div>
                        <div class="ap-section-rule-diamond"></div>
                        <div class="ap-section-rule-line"></div>
                    </div>
                    <span class="ap-section-label">Testimonials</span>
                    <h2 class="ap-section-title">What clients <em>are saying</em></h2>

                    <div v-for="item in testimonials" :key="item.id" class="ap-testimonial-card">
                        <div v-if="item.approved_at">
                            <p class="ap-testimonial-text">
                                <span v-if="get_testimonial_length(item.text) > 399 && !checkifActivated(item.id)"
                                      v-html="fixHtml(stripHtml(item.text).slice(0, 400))"></span>
                                <a v-if="get_testimonial_length(item.text) > 399 && !checkifActivated(item.id)"
                                   @click="activateReadMore(item.id)" class="ap-read-more">Read more...</a>
                                <span v-if="get_testimonial_length(item.text) <= 399 || checkifActivated(item.id)"
                                      v-html="item.text"></span>
                            </p>
                            <div class="ap-testimonial-author" v-if="item.user_nickname">
                                <div class="ap-testimonial-dot"></div>
                                <span class="ap-testimonial-name">{{ item.user_nickname }}</span>
                            </div>
                        </div>
                    </div>

                    <div class="ap-testimonial-actions">
                        <a v-if="checkLength()" :href="testimonial_url" class="ap-btn-ghost-dark">
                            See All Testimonials <i class="fas fa-arrow-right"></i>
                        </a>
                        <button v-if="canEdit" class="ap-btn-ghost-dark" @click="$router.push(edit_route)">
                            Manage Testimonials
                        </button>
                        <button v-else class="ap-btn-ghost-dark" @click="writeTestimonial">
                            Write a Testimonial
                        </button>
                    </div>
                </section>

                <!-- No testimonials -->
                <section class="ap-testimonials-section" v-if="(!testimonials || testimonials.length === 0) && !editable && agent_name">
                    <div class="ap-section-rule">
                        <div class="ap-section-rule-line"></div>
                        <div class="ap-section-rule-diamond"></div>
                        <div class="ap-section-rule-line"></div>
                    </div>
                    <span class="ap-section-label">Testimonials</span>
                    <h2 class="ap-section-title">What clients <em>are saying</em></h2>
                    <p class="ap-about-text">No testimonials yet.</p>
                    <button v-if="!canEdit" class="ap-btn-ghost-dark mt-3" @click="writeTestimonial">
                        Write a Testimonial about {{ firstName }}
                    </button>
                </section>

            </div>

            <!-- Sidebar -->
            <aside class="ap-sidebar" v-if="!editable">
                <!-- Contact Info Card -->
                <div class="ap-contact-card" v-if="agent_name">
                    <div class="ap-contact-card-header">
                        <div class="ap-contact-card-name">{{ agent_name }}</div>
                        <div class="ap-contact-card-title">Real Estate Agent · Louisville, KY</div>
                    </div>
                    <div class="ap-contact-card-body">
                        <div class="ap-contact-row" v-if="agent.phone">
                            <div class="ap-contact-icon"><i class="fas fa-phone"></i></div>
                            <a :href="'tel:' + agent.phone">{{ agent.phone }}</a>
                        </div>
                        <div class="ap-contact-row" v-if="agent.user && agent.user.email">
                            <div class="ap-contact-icon"><i class="fas fa-envelope"></i></div>
                            <a :href="'mailto:' + agent.user.email">{{ agent.user.email }}</a>
                        </div>
                        <div class="ap-contact-row" v-if="agent.instagram_link || agent.linkedin_link || agent.twitter_link">
                            <div class="ap-contact-icon"><i class="fas fa-share-alt"></i></div>
                            <div class="ap-social-links">
                                <a v-if="agent.instagram_link" :href="agent.instagram_link" target="_blank"><i class="fab fa-instagram"></i></a>
                                <a v-if="agent.linkedin_link" :href="agent.linkedin_link" target="_blank"><i class="fab fa-linkedin"></i></a>
                                <a v-if="agent.twitter_link" :href="agent.twitter_link" target="_blank"><i class="fab fa-twitter"></i></a>
                            </div>
                        </div>
                    </div>
                </div>
            </aside>
        </div>

        <!-- Listings (full width, outside the 2-col grid) -->
        <section class="ap-listings-section" id="listings" v-if="!editable">
            <div class="ap-section-rule">
                <div class="ap-section-rule-line"></div>
                <div class="ap-section-rule-diamond"></div>
                <div class="ap-section-rule-line"></div>
            </div>
            <span class="ap-section-label">Portfolio</span>
            <h2 class="ap-section-title">{{ firstName }}'s <em>listings</em></h2>

            <div class="ap-listings-filter">
                <button class="ap-filter-btn" :class="{ active: selected === null }"
                        @click="selected = null">Both</button>
                <button class="ap-filter-btn" :class="{ active: selected === '1' }"
                        @click="selected = '1'">Listing Agent</button>
                <button class="ap-filter-btn" :class="{ active: selected === '2' }"
                        @click="selected = '2'">Buyer's Agent</button>
            </div>

            <browse :listing_type="listing_route" :listing_type_id="selected"></browse>
        </section>

        <!-- Loading overlay -->
        <div class="ap-loading" v-if="isLoading">
            <i class="fa fa-spinner fa-pulse fa-3x fa-fw"></i>
        </div>

        <!-- Testimonial Modal -->
        <b-modal id="testimonialModal" ref="testimonialModal" title="Write a Testimonial" size="lg">
            <write-testimonial ref="writeTestimonial" :agent="agent"></write-testimonial>
            <div slot="modal-footer" class="w-100"></div>
        </b-modal>
    </div>
</template>

<script>
export default {
    name: 'agent-profile',

    created() {
        this.getAgent();
        this.getMe();
    },

    data() {
        return {
            agent_name: null,
            agent: {
                user: { name: null },
                testimonials: []
            },
            editable: false,
            canEdit: false,
            listing_route: "agents/listings/" + this.$route.params.slug,
            activatedArray: [],
            selected: null,
            isLoading: false,
            testimonials: null,
            edit_route: "/admin/agents/testimonials/" + this.$route.params.slug
        }
    },

    computed: {
        firstName() {
            if (!this.agent_name) return '';
            return this.agent_name.split(' ')[0];
        },
        lastName() {
            if (!this.agent_name) return '';
            const parts = this.agent_name.split(' ');
            return parts.slice(1).join(' ');
        },
        agent_image() {
            if (this.agent && this.agent.image_url) {
                return this.agent.image_url + "?fit=facearea&facepad=5&w=400&h=480";
            }
            return "https://forman-realty-group.imgix.net/no_image_available.jpg?w=400&h=480";
        },
        testimonial_url() {
            if (this.agent && this.agent.slug) {
                return "/" + this.agent.slug + "/testimonials";
            }
            return '#';
        },
    },

    methods: {
        getAgent() {
            axios.get('/api/agents/' + this.$route.params.slug)
                .then((response) => {
                    this.agent = response.data;
                    this.user = response.data.user;
                    this.agent_name = response.data.user.name;
                    this.getTestimonials(this.agent.id);
                });
        },

        getMe() {
            axios.get('/api/me/')
                .then((response) => {
                    if (response.data && response.data.agent) {
                        this.canEdit = response.data.agent.slug === this.$route.params.slug;
                    }
                })
                .catch(() => {});
        },

        getTestimonials(id) {
            axios.get('/api/agents/testimonials/' + id)
                .then(response => {
                    if (response.data && response.data.testimonials) {
                        this.testimonials = response.data.testimonials.slice(0, 3);
                    }
                })
                .catch(() => {});
        },

        submitBio() {
            this.isLoading = true;
            axios.post('/api/agents/update', { agent: this.agent })
                .then((response) => {
                    this.isLoading = false;
                    if (response.data.status === false) {
                        this.toast(response.data.message, 'danger');
                    } else {
                        this.toast('Bio Updated!', 'success');
                        this.editable = false;
                    }
                });
        },

        writeTestimonial() {
            this.$refs['testimonialModal'].show();
        },

        toast(message, variant) {
            this.$bvToast.toast(message, {
                title: variant,
                variant: variant,
                solid: true
            });
        },

        checkifActivated(id) {
            return this.activatedArray.includes(id);
        },

        activateReadMore(id) {
            this.activatedArray.push(id);
        },

        checkLength() {
            return this.testimonials && this.testimonials.length > 3;
        },

        get_testimonial_length(text) {
            return this.stripHtml(text).length;
        },

        stripHtml(text) {
            return text.replace(/<[^>]+>/g, '');
        },

        fixHtml(html) {
            var div = document.createElement('div');
            div.innerHTML = html;
            return div.innerHTML;
        },
    },

    mounted() {
        this.$eventHub.$on('testimonial::added', () => {
            this.$refs['testimonialModal'].hide();
            this.toast('Your testimonial has been submitted!', 'success');
        });
    },
}
</script>

<style lang="scss">
// Brand variables
$ap-blue:       rgb(18, 86, 151);
$ap-blue-deep:  rgb(10, 48, 86);
$ap-gold:       #C8A84B;
$ap-gold-lt:    #DCC278;
$ap-offwhite:   #F7F9FC;
$ap-muted:      #607080;
$ap-border:     rgba(18,86,151,0.12);

.agent-profile-page {
    background: $ap-offwhite;
    min-height: 100vh;
}

// ── BREADCRUMB ──
.ap-breadcrumb {
    padding: 16px 60px;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12.5px;
    color: rgba(255,255,255,0.7);
    background: $ap-blue;
    border-bottom: none;

    a {
        color: #fff;
        text-decoration: none;
        font-weight: 500;
        &:hover { color: $ap-gold; text-decoration: none; }
    }

    > span:last-child {
        color: $ap-gold;
        font-weight: 500;
    }
}
.ap-breadcrumb-sep { color: rgba(255,255,255,0.35); }

// ── HERO ──
.ap-hero {
    background: $ap-blue;
    position: relative;
    overflow: hidden;
    padding: 64px 60px 0;

    &::before {
        content: '';
        position: absolute;
        top: -80px; right: -80px;
        width: 480px; height: 480px;
        border-radius: 50%;
        background: rgba(255,255,255,0.03);
        pointer-events: none;
    }
    &::after {
        content: '';
        position: absolute;
        bottom: 0; left: 0; right: 0;
        height: 2px;
        background: linear-gradient(90deg, transparent 5%, $ap-gold 50%, transparent 95%);
        opacity: 0.5;
    }
}

.ap-hero-inner {
    max-width: 1100px;
    margin: 0 auto;
    display: grid;
    grid-template-columns: 260px 1fr;
    gap: 48px;
    align-items: flex-end;
}

.ap-hero-photo-wrap {
    position: relative;
    align-self: flex-end;
}
.ap-hero-photo {
    width: 260px;
    height: 320px;
    object-fit: cover;
    object-position: top;
    display: block;
    border-radius: 4px 4px 0 0;
    filter: grayscale(15%);
}
.ap-hero-photo-frame {
    position: absolute;
    top: -12px; left: -12px;
    width: 60px; height: 60px;
    border-top: 2px solid $ap-gold;
    border-left: 2px solid $ap-gold;
    opacity: 0.6;
    border-radius: 2px;
    pointer-events: none;
}

.ap-hero-text {
    padding-bottom: 40px;
    display: flex;
    flex-direction: column;
}

.ap-hero-label {
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.25em;
    text-transform: uppercase;
    color: $ap-gold;
    margin-bottom: 12px;
}

.ap-hero-name {
    font-family: 'Cormorant Garamond', serif;
    font-size: 62px;
    font-weight: 300;
    color: #fff;
    line-height: 1.0;
    margin-bottom: 16px;
    em { font-style: italic; color: $ap-gold-lt; }
}

.ap-hero-title {
    font-size: 12px;
    font-weight: 500;
    letter-spacing: 0.15em;
    text-transform: uppercase;
    color: rgba(255,255,255,0.5);
    margin-bottom: 28px;
}

.ap-hero-actions {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
}

.ap-btn-primary {
    font-family: 'Jost', sans-serif;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    background: $ap-gold;
    color: $ap-blue-deep;
    border: none;
    border-radius: 4px;
    padding: 12px 24px;
    cursor: pointer;
    text-decoration: none;
    transition: background 0.2s;
    white-space: nowrap;
    display: inline-block;
    text-align: center;
    &:hover { background: $ap-gold-lt; color: $ap-blue-deep; text-decoration: none; }
}

.ap-btn-ghost {
    font-family: 'Jost', sans-serif;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    background: none;
    color: rgba(255,255,255,0.8);
    border: 1.5px solid rgba(255,255,255,0.25);
    border-radius: 4px;
    padding: 12px 24px;
    cursor: pointer;
    text-decoration: none;
    transition: border-color 0.2s, color 0.2s;
    white-space: nowrap;
    &:hover { border-color: rgba(255,255,255,0.6); color: #fff; text-decoration: none; }
}

.ap-btn-ghost-dark {
    font-family: 'Jost', sans-serif;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    background: none;
    color: $ap-blue;
    border: 1.5px solid $ap-border;
    border-radius: 4px;
    padding: 10px 20px;
    cursor: pointer;
    text-decoration: none;
    transition: border-color 0.2s, background 0.2s;
    &:hover { border-color: $ap-blue; background: rgba(18,86,151,0.04); text-decoration: none; }
}

.ap-btn-block { display: block; width: 100%; }

// ── BODY LAYOUT ──
.ap-body {
    max-width: 1100px;
    margin: 0 auto;
    padding: 48px 60px;
    display: grid;
    grid-template-columns: 1fr 320px;
    gap: 40px;
}

// ── SECTION STYLES ──
.ap-section-label {
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.22em;
    text-transform: uppercase;
    color: $ap-gold;
    margin-bottom: 12px;
    display: block;
}

.ap-section-title {
    font-family: 'Cormorant Garamond', serif;
    font-size: 34px;
    font-weight: 300;
    color: $ap-blue;
    margin-bottom: 20px;
    line-height: 1.15;
    em { font-style: italic; }
}

.ap-about-text {
    font-size: 15px;
    font-weight: 300;
    line-height: 1.8;
    color: $ap-muted;
    p { margin-bottom: 1em; }
}

// ── SECTION RULE ──
.ap-section-rule {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 28px;
}
.ap-section-rule-line { flex: 1; height: 1px; background: $ap-border; }
.ap-section-rule-diamond {
    width: 6px; height: 6px;
    background: $ap-gold;
    transform: rotate(45deg);
    opacity: 0.7;
    flex-shrink: 0;
}

// ── TESTIMONIALS ──
.ap-testimonials-section { margin-top: 40px; }

.ap-testimonial-card {
    background: #fff;
    border: 1px solid $ap-border;
    border-left: 3px solid $ap-gold;
    border-radius: 0 8px 8px 0;
    padding: 28px 28px 24px;
    margin-bottom: 16px;
    position: relative;

    &::before {
        content: '\201C';
        font-family: 'Cormorant Garamond', serif;
        font-size: 80px;
        font-weight: 300;
        color: $ap-gold;
        opacity: 0.2;
        position: absolute;
        top: 4px; left: 18px;
        line-height: 1;
        pointer-events: none;
    }
}

.ap-testimonial-text {
    font-size: 14.5px;
    font-weight: 300;
    line-height: 1.75;
    color: $ap-muted;
    margin-bottom: 16px;
    position: relative;
    z-index: 1;
}

.ap-read-more {
    color: $ap-blue;
    cursor: pointer;
    font-style: italic;
    font-size: 13px;
    &:hover { color: $ap-gold; }
}

.ap-testimonial-author {
    display: flex;
    align-items: center;
    gap: 10px;
}
.ap-testimonial-dot {
    width: 20px; height: 1px;
    background: $ap-gold;
    opacity: 0.6;
}
.ap-testimonial-name {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: $ap-blue;
}

.ap-testimonial-actions {
    margin-top: 20px;
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
}

// ── LISTINGS ──
.ap-listings-section {
    max-width: 1100px;
    margin: 0 auto;
    padding: 0 60px 48px;

    .browse {
        padding: 0 !important;
        max-width: 100% !important;

        > .row > .col-md-12 > .card {
            padding: 0 !important;
            margin: 0 !important;
            border: none !important;
            background: transparent !important;
        }
    }

    .listing-grid--desktop {
        grid-template-columns: repeat(2, 1fr);
        padding: 0;
    }
}

.ap-listings-filter {
    display: flex;
    gap: 8px;
    margin-bottom: 24px;
    flex-wrap: wrap;
}

.ap-filter-btn {
    font-family: 'Jost', sans-serif;
    font-size: 11.5px;
    font-weight: 500;
    letter-spacing: 0.08em;
    padding: 7px 16px;
    border-radius: 20px;
    cursor: pointer;
    border: 1.5px solid $ap-border;
    background: #fff;
    color: $ap-muted;
    transition: all 0.2s;

    &.active, &:hover {
        background: $ap-blue;
        color: #fff;
        border-color: $ap-blue;
    }
}

// ── SIDEBAR ──
.ap-sidebar {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.ap-contact-card {
    background: #fff;
    border: 1px solid $ap-border;
    border-radius: 8px;
    overflow: hidden;
}

.ap-contact-card-header {
    background: $ap-blue;
    padding: 20px 24px;
    position: relative;

    &::after {
        content: '';
        position: absolute;
        bottom: 0; left: 0; right: 0;
        height: 2px;
        background: $ap-gold;
        opacity: 0.5;
    }
}

.ap-contact-card-name {
    font-family: 'Cormorant Garamond', serif;
    font-size: 22px;
    font-weight: 300;
    color: #fff;
    margin-bottom: 4px;
}

.ap-contact-card-title {
    font-size: 10px;
    font-weight: 500;
    letter-spacing: 0.15em;
    text-transform: uppercase;
    color: rgba(255,255,255,0.45);
}

.ap-contact-card-body {
    padding: 20px 24px;
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.ap-contact-row {
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 13.5px;
    color: $ap-muted;

    a {
        color: $ap-blue;
        text-decoration: none;
        font-weight: 500;
        &:hover { color: $ap-gold; text-decoration: none; }
    }
}

.ap-contact-icon {
    width: 32px; height: 32px;
    border-radius: 6px;
    background: $ap-offwhite;
    border: 1px solid $ap-border;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    font-size: 14px;
    color: $ap-blue;
}

.ap-social-links {
    display: flex;
    gap: 12px;
    a {
        color: $ap-blue;
        font-size: 16px;
        transition: color 0.2s;
        &:hover { color: $ap-gold; }
    }
}

// ── LOADING ──
.ap-loading {
    position: fixed;
    top: 0; left: 0; right: 0; bottom: 0;
    background: rgba(255,255,255,0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
}

// ── RESPONSIVE ──
@media (max-width: 960px) {
    .ap-hero-inner {
        grid-template-columns: 200px 1fr;
        gap: 32px;
    }
    .ap-hero-name { font-size: 46px; }
    .ap-hero-photo { width: 200px; height: 260px; }
    .ap-body {
        grid-template-columns: 1fr;
        padding: 32px 24px;
    }
    .ap-sidebar { order: -1; }
    .ap-breadcrumb, .ap-hero { padding-left: 24px; padding-right: 24px; }
}

@media (max-width: 600px) {
    .ap-hero-inner { grid-template-columns: 1fr; }
    .ap-hero-photo {
        width: 100%;
        height: 280px;
        border-radius: 4px;
    }
    .ap-hero-name { font-size: 40px; }
    .ap-hero { padding-top: 32px; }
    .ap-breadcrumb { padding: 12px 16px; }
    .ap-body { padding: 24px 16px; }
}
</style>
